import { Link, NavLink, Outlet, Navigate, useLocation } from 'react-router-dom';
import { Package, Receipt, Plus, Tags, Truck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import LogoutButton from './LogoutButton';
import StoreSwitch from './admin/StoreSwitch';

// Sidebar link with a real active state. These were plain <Link> elements with
// no active styling, so the sidebar never showed which page you were on — and
// one of them was hardcoded to the accent colour, which made it look permanently
// selected from every other screen.
function SideLink({ to, children }) {
  return (
    <NavLink
      to={to}
      end={to === '/admin/products'} // otherwise /admin/products/add keeps Products lit
      style={({ isActive }) => ({
        padding: '8px 10px',
        borderRadius: 8,
        color: isActive ? 'var(--accent)' : 'var(--text)',
        background: isActive ? 'var(--surface-hover)' : 'transparent',
        fontWeight: isActive ? 800 : 600,
      })}
    >
      {children}
    </NavLink>
  );
}

// Admin area is the one part of ShinyVault that requires login — gated on
// profiles.is_shinyvault_admin. Trainer Center shares this database and every
// person has one account across both sites, but admin privilege is per-app:
// having Trainer Center's is_admin grants nothing here. RLS enforces the same
// split server-side, so this gate is UX, not the security boundary.
//
// Two shells in one (Chase 2026-07-19): desktop keeps the sidebar; on
// phones the sidebar disappears and the admin becomes an iOS-style app —
// slim top bar, content, and a fixed bottom tab bar with a prominent
// center "+" that jumps straight to a new product. CSS switching lives in
// index.css under "admin (mobile app shell)".
//
// Five slots is the most that fits a 360px phone, and packing happens on a
// phone at the bench — so Fulfill takes the slot the store link used to have
// and the store link moved up to the top bar, which is always visible anyway.
const TABS = [
  { to: '/admin/products', label: 'Products', icon: Package },
  { to: '/admin/orders', label: 'Orders', icon: Receipt },
  { to: '/admin/products/add', label: 'Add', icon: Plus, primary: true },
  { to: '/admin/fulfillment', label: 'Fulfill', icon: Truck },
  { to: '/admin/categories', label: 'Categories', icon: Tags },
];

export default function AdminLayout() {
  const { user, isAdmin, isOwner, isConsignor, isLoading } = useAuth();
  const { pathname, search } = useLocation();
  // The "+" owns the highlight during the whole add sequence (add / bulk / new,
  // plus editing an item inside a bulk batch), so Products isn't lit up then.
  const inAddFlow =
    ['/admin/products/add', '/admin/products/bulk', '/admin/products/new'].includes(pathname) ||
    (/^\/admin\/products\/[^/]+$/.test(pathname) && search.includes('from=bulk'));

  if (isLoading) return <div className="sv-container" style={{ padding: 60 }}>Loading…</div>;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(pathname)}`} replace />;
  if (!isAdmin) {
    return (
      <div className="sv-container" style={{ padding: 60, textAlign: 'center' }}>
        Staff only.
      </div>
    );
  }

  return (
    <div className="sv-admin-wrap" style={{ display: 'flex', minHeight: '100vh' }}>
      <aside
        className="sv-admin-sidebar"
        style={{
          width: 220, borderRight: '1px solid var(--border)', padding: '24px 16px',
          background: 'var(--bg-elevated)', flexShrink: 0,
        }}
      >
        <div style={{ fontWeight: 900, marginBottom: 24, fontSize: '1.05rem' }}>Shiny Vault LGS Admin</div>
        <nav style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.9rem', fontWeight: 600 }}>
          <SideLink to="/admin/products">Products</SideLink>
          <SideLink to="/admin/categories">Categories</SideLink>
          <SideLink to="/admin/orders">Orders</SideLink>
          <SideLink to="/admin/fulfillment">Fulfillment</SideLink>
          <SideLink to="/admin/shipping-boxes">Shipping boxes</SideLink>
          <SideLink to="/admin/payouts">Payouts</SideLink>
          <SideLink to="/admin/analytics">Analytics</SideLink>
          {/* A consignor who is also staff (Olin helps run the shop and sells
              his own stock) would otherwise never find this: the header menu
              shows "Staff Area" instead of "My Account" for anyone with the
              admin flag, so /account/banking has no link anywhere he goes. */}
          {isConsignor && <SideLink to="/account/banking">My Banking</SideLink>}
          {/* Chase only — an email match, so no amount of admin privilege reveals it.
              Marked with a dot rather than accent-coloured text: it used to be
              permanently purple, which is what the active page looks like, so
              every screen appeared to be on Influencer area. */}
          {isOwner && (
            <SideLink to="/admin/influencers">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--accent)', flexShrink: 0 }} />
                Influencer area
              </span>
            </SideLink>
          )}
          <Link to="/" style={{ padding: '8px 10px', borderRadius: 8, color: 'var(--text-muted)', marginTop: 12 }}>
            ← Back to store
          </Link>
          <LogoutButton
            iconSize={16}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '8px 10px', borderRadius: 8, marginTop: 4,
              background: 'transparent', border: 'none', color: 'var(--text-muted)',
              fontSize: '0.9rem', fontWeight: 600, textAlign: 'left',
            }}
          />
        </nav>
      </aside>

      <div className="sv-admin-topbar">
        <span style={{ fontWeight: 900 }}>Shiny Vault <span style={{ color: 'var(--accent)' }}>Admin</span></span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <Link to="/admin/shipping-boxes" style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontWeight: 600 }}>
            Shipping
          </Link>
          <Link to="/" style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontWeight: 600 }}>
            Store
          </Link>
          <LogoutButton
            label=""
            iconSize={18}
            aria-label="Log out"
            style={{ display: 'inline-flex', alignItems: 'center', background: 'transparent', border: 'none', color: 'var(--text-muted)', padding: 0 }}
          />
        </div>
      </div>

      <main className="sv-admin-main" style={{ flex: 1, padding: '28px 32px' }}>
        <StoreSwitch />
        <Outlet />
      </main>

      <nav className="sv-admin-tabbar" aria-label="Admin navigation">
        {TABS.map(({ to, label, icon: Icon, primary }) => {
          if (primary) {
            return (
              <Link key={to} to={to} className="sv-admin-tab sv-admin-tab--add" aria-label="Add product" aria-current={inAddFlow ? 'page' : undefined}>
                <span
                  className="sv-admin-tab-addbtn"
                  style={inAddFlow ? { boxShadow: '0 0 0 4px color-mix(in srgb, var(--accent) 35%, transparent)', transform: 'scale(1.06)' } : undefined}
                >
                  <Icon size={26} />
                </span>
              </Link>
            );
          }
          const active = (pathname === to || (to !== '/' && pathname.startsWith(to))) && !inAddFlow;
          return (
            <Link key={to} to={to} className="sv-admin-tab" style={{ color: active ? 'var(--accent)' : 'var(--text-muted)' }}>
              <Icon size={21} />
              <span>{label}</span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
